import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth, appLogout } from '@/hooks/useAppAuth';
import { useNotifications } from '@/hooks/useNotifications';
import CardRegistrationModal, { type CardFormData as CardFormDataT } from '@/components/feature/CardRegistrationModal';

type CardType = 'credit' | 'check';

export default function AppProfilePage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [profile, setProfile] = useState<{
    name: string;
    phone: string;
    gender: string;
    birth_date: string;
    address: string;
    caregiver_number: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCsModal, setShowCsModal] = useState(false);
  const [showNoticesModal, setShowNoticesModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardSaving, setCardSaving] = useState(false);
  const [cardSuccess, setCardSuccess] = useState(false);
  const [registeredCard, setRegisteredCard] = useState<{ cardType: CardType; last4: string } | null>(null);
  const { unreadCount } = useNotifications();

  useEffect(() => {
    if (!auth.userId) return;
    setLoading(true);
    Promise.all([
      supabase.from('profiles')
        .select('name, phone, gender, birth_date, address, caregiver_number, roles, role')
        .eq('id', auth.userId)
        .maybeSingle(),
      supabase.from('caregiver_profiles')
        .select('payment_card')
        .eq('user_id', auth.userId)
        .maybeSingle(),
    ]).then(([profileResult, cardResult]) => {
      if (profileResult.data) setProfile(profileResult.data);
      if (cardResult.data?.payment_card) {
        const pc = cardResult.data.payment_card as { card_type: CardType; last4: string };
        setRegisteredCard({ cardType: pc.card_type, last4: pc.last4 });
      }
      setLoading(false);
    });
  }, [auth.userId]);

  const resolvedRoles: string[] = profile ? (
    (profile as unknown as { roles?: string[]; role?: string }).roles?.length
      ? (profile as unknown as { roles: string[] }).roles
      : (profile as unknown as { role?: string }).role ? [(profile as unknown as { role: string }).role] : []
  ) : [];

  async function handleLogout() {
    await appLogout();
    navigate('/app', { replace: true });
  }

  async function handleCardRegister(cardType: CardType, formData: CardFormDataT) {
    setCardSaving(true);
    try {
      const last4 = formData.cardNumber4;
      const paymentCard = {
        card_type: cardType,
        last4,
        updated_at: new Date().toISOString(),
      };
      if (auth.userId) {
        await supabase.from('caregiver_profiles')
          .upsert({ user_id: auth.userId, payment_card: paymentCard, updated_at: new Date().toISOString() });
      }
      setRegisteredCard({ cardType, last4 });
      setCardSuccess(true);
      setCardSaving(false);
    } catch (err) {
      console.error('Failed to save card:', err);
      setCardSaving(false);
    }
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const quickActions = [
    {
      icon: 'ri-customer-service-2-line',
      label: '고객센터',
      onClick: () => setShowCsModal(true),
    },
    {
      icon: 'ri-notification-3-line',
      label: '공지사항',
      onClick: () => setShowNoticesModal(true),
    },
    {
      icon: 'ri-chat-3-line',
      label: '카카오톡',
      onClick: () => window.open('https://pf.kakao.com/_SNpdX/chat', '_blank'),
    },
    {
      icon: 'ri-settings-4-line',
      label: '알림설정',
      onClick: () => navigate('/app/profile/notifications'),
    },
  ];

  const serviceSettings = [
    { label: '결제 카드 관리', onClick: () => setShowCardModal(true), icon: 'ri-bank-card-line' },
    { label: '알림 내역', onClick: () => navigate('/app/notifications') },
    { label: '알림 설정', onClick: () => navigate('/app/profile/notifications') },
    { label: '매칭 내역', onClick: () => navigate('/app/matching') },
    { label: '내 리뷰', onClick: () => navigate('/app/my-reviews') },
  ];

  const serviceInquiry = [
    {
      label: '카카오톡 문의하기',
      onClick: () => window.open('https://pf.kakao.com/_SNpdX/chat', '_blank'),
    },
    { label: '자주하는 질문', onClick: () => navigate('/app/faq') },
  ];

  const etcItems = [
    { label: '고객센터', onClick: () => setShowCsModal(true) },
    { label: '공지사항', onClick: () => setShowNoticesModal(true) },
    { label: '약관 및 정책', onClick: () => navigate('/app/terms') },
    { label: '개인정보 처리방침', onClick: () => navigate('/app/privacy') },
    { label: '앱 버전 정보', onClick: () => {}, value: 'v1.0.0' },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-[#F8F9FA]">
        {/* 상단 헤더 */}
        <div className="px-5 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/85651164-9881-44bb-8cd7-002bfdcb405b/ccf5aafb-c3da-4b67-8f64-1b7bcab82f90__smallsize.png?v=7d34dd8d91c49a026e06eeef1279073c"
                alt="코리아케어러스"
                className="w-5 h-5 object-contain"
              />
            </div>
            <h1 className="text-lg font-extrabold text-gray-900">코리아케어러스</h1>
          </div>
          <button
            onClick={() => navigate('/app/notifications')}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors relative"
          >
            <i className="ri-notification-3-line text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* 사용자 이름 카드 */}
        <div className="px-5 mb-5">
          <button
            onClick={() => navigate('/app/profile/edit')}
            className="w-full flex items-center justify-between py-4 cursor-pointer text-left"
          >
            <div>
              <span className="text-2xl font-extrabold text-gray-900">
                {profile?.name || '사용자'}님
              </span>
              <span className="text-sm text-gray-400 ml-2">
                {resolvedRoles.includes('caregiver') ? '간병인' : '보호자'}
              </span>
            </div>
            <i className="ri-arrow-right-s-line text-gray-300 text-xl" />
          </button>
        </div>

        {/* 4개 퀵액션 아이콘 */}
        <div className="px-5 mb-6">
          <div className="grid grid-cols-4 gap-3">
            {quickActions.map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className="flex flex-col items-center gap-2 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                  <i className={`${item.icon} text-xl text-gray-700`} />
                </div>
                <span className="text-[11px] text-gray-600 font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 서비스 설정 */}
        <div className="px-5 mb-6">
          <p className="text-xs text-gray-400 font-medium mb-3 px-1">서비스 설정</p>
          <div className="bg-white rounded-2xl overflow-hidden">
            {serviceSettings.map((item, i) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors ${i < serviceSettings.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  {(item as { icon?: string }).icon && (
                    <i className={`${(item as { icon: string }).icon} text-gray-400 text-sm`} />
                  )}
                  <span className="text-sm text-gray-800 font-medium">{item.label}</span>
                  {item.label === '결제 카드 관리' && registeredCard && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E8F5E9] text-[#2E7D6B] font-bold">
                      {registeredCard.cardType === 'credit' ? '신용' : '체크'} ****{registeredCard.last4}
                    </span>
                  )}
                </div>
                <i className="ri-arrow-right-s-line text-gray-300" />
              </button>
            ))}
          </div>
        </div>

        {/* 서비스 문의 */}
        <div className="px-5 mb-6">
          <p className="text-xs text-gray-400 font-medium mb-3 px-1">서비스 문의</p>
          <div className="bg-white rounded-2xl overflow-hidden">
            {serviceInquiry.map((item, i) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors ${i < serviceInquiry.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <span className="text-sm text-gray-800 font-medium">{item.label}</span>
                <i className="ri-arrow-right-s-line text-gray-300" />
              </button>
            ))}
          </div>
        </div>

        {/* 기타 */}
        <div className="px-5 mb-6">
          <p className="text-xs text-gray-400 font-medium mb-3 px-1">기타</p>
          <div className="bg-white rounded-2xl overflow-hidden">
            {etcItems.map((item, i) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className={`w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors ${i < etcItems.length - 1 ? 'border-b border-gray-50' : ''}`}
              >
                <span className="text-sm text-gray-800 font-medium">{item.label}</span>
                <div className="flex items-center gap-1">
                  {(item as { value?: string }).value && (
                    <span className="text-xs text-gray-400">{(item as { value: string }).value}</span>
                  )}
                  <i className="ri-arrow-right-s-line text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 로그아웃 */}
        <div className="px-5 mb-6">
          <button
            onClick={handleLogout}
            className="w-full py-4 rounded-2xl text-sm font-bold text-gray-500 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
          >
            로그아웃
          </button>
        </div>

        {/* 카드 등록 모달 */}
        <CardRegistrationModal
          isOpen={showCardModal}
          onClose={() => { if (!cardSaving) { setShowCardModal(false); setCardSuccess(false); } }}
          onRegister={handleCardRegister}
          saving={cardSaving}
          success={cardSuccess}
        />

        {/* 고객센터 모달 */}
        {showCsModal && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={() => setShowCsModal(false)}>
            <div
              className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-slide-up max-h-[90vh] md:max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">고객센터</h2>
                <button onClick={() => setShowCsModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-5">
                궁금하신 점이 있으시면 아래 방법으로 문의해주세요.<br />
                친절하고 빠르게 도와드리겠습니다.
              </p>

              <div className="space-y-3 mb-5">
                <a
                  href="https://pf.kakao.com/_SNpdX/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-xl border border-[#FEE500] bg-[#FEE500] cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <div className="w-10 h-10 rounded-xl bg-black/10 flex items-center justify-center">
                    <i className="ri-chat-3-line text-xl text-black/70" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-black/80">카카오톡 1:1 채팅</p>
                    <p className="text-xs text-black/50">실시간으로 빠르게 상담받기</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-black/40" />
                </a>

                <a
                  href="tel:15668654"
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#f0f9f6] flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-[#2E7D6B]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">전화 상담</p>
                    <p className="text-xs text-gray-400">1566-8654</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300" />
                </a>

                <button
                  onClick={() => { setShowCsModal(false); navigate('/app/faq'); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E3F2FD] flex items-center justify-center">
                    <i className="ri-question-answer-line text-xl text-[#1976D2]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-bold text-gray-900">자주 묻는 질문</p>
                    <p className="text-xs text-gray-400">FAQ에서 빠르게 답변 찾기</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300" />
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                운영시간: 평일 09:00 ~ 18:00 (주말/공휴일 제외)
              </p>
            </div>
          </div>
        )}

        {/* 공지사항 모달 */}
        {showNoticesModal && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={() => setShowNoticesModal(false)}>
            <div
              className="bg-white w-full max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-slide-up max-h-[90vh] md:max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-extrabold text-gray-900">공지사항</h2>
                <button onClick={() => setShowNoticesModal(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200">
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {[
                  { id: 1, title: '코리아케어러스 앱 서비스 오픈 안내', date: '2026.06.10', category: '공지', isNew: true },
                  { id: 2, title: '2026년 6월 서비스 이용약관 개정 안내', date: '2026.06.15', category: '약관', isNew: true },
                  { id: 3, title: '간병인 자격증 등록 시 주의사항', date: '2026.06.08', category: '안내', isNew: false },
                  { id: 4, title: '여름철 간병 시 주의사항 안내', date: '2026.06.05', category: '안내', isNew: false },
                ].map((n) => (
                  <div key={n.id} className="p-4 rounded-xl bg-gray-50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">
                        {n.category}
                      </span>
                      {n.isNew && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-500 font-bold">
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1">{n.title}</p>
                    <p className="text-xs text-gray-400">{n.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </div>
    </AppLayout>
  );
}